import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  MessageSquare, Briefcase, Mail, Phone, Building, Clock, 
  RefreshCw, Search, ChevronDown, ChevronUp, Trash2, Eye,
  User, FileText, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSubmissionsSection = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [typeFilter, setTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const [counts, setCounts] = useState({ total: 0, general: 0, investor: 0 });

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const url = new URL(`${API_URL}/api/admin/contact-submissions`);
      if (typeFilter) url.searchParams.append('enquiry_type', typeFilter);
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to fetch submissions');
      
      setSubmissions(data.submissions);
      setCounts({
        total: data.total,
        general: data.general_count,
        investor: data.investor_count
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, typeFilter]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleDelete = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/contact-submissions/${submissionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to delete');
      }
      
      toast.success('Submission deleted');
      fetchSubmissions();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const filteredSubmissions = submissions.filter(s => 
    !searchQuery || 
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.message?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.organization?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Total Submissions</span>
            <MessageSquare className="text-blue-500" size={20} />
          </div>
          <p className="text-2xl font-bold mt-2">{counts.total}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Contact Us</span>
            <Mail className="text-green-500" size={20} />
          </div>
          <p className="text-2xl font-bold mt-2">{counts.general}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Investor Corner</span>
            <Briefcase className="text-purple-500" size={20} />
          </div>
          <p className="text-2xl font-bold mt-2">{counts.investor}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex gap-2">
          <button 
            onClick={() => setTypeFilter('')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              !typeFilter ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({counts.total})
          </button>
          <button 
            onClick={() => setTypeFilter('general')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              typeFilter === 'general' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Mail size={16} />
            Contact Us ({counts.general})
          </button>
          <button 
            onClick={() => setTypeFilter('investor')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              typeFilter === 'investor' ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Briefcase size={16} />
            Investor Corner ({counts.investor})
          </button>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search submissions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={fetchSubmissions}
            className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Submissions List */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="animate-spin mx-auto text-blue-500" size={32} />
          <p className="text-gray-500 mt-2">Loading submissions...</p>
        </div>
      ) : filteredSubmissions.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <MessageSquare className="mx-auto text-gray-400" size={48} />
          <p className="text-gray-500 mt-2">No submissions found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredSubmissions.map((submission) => (
            <div key={submission.id} className="bg-white rounded-xl shadow-sm border overflow-hidden">
              {/* Submission Header */}
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedSubmission(expandedSubmission === submission.id ? null : submission.id)}
              >
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${
                    submission.enquiry_type === 'investor' 
                      ? 'bg-purple-100' 
                      : 'bg-green-100'
                  }`}>
                    {submission.enquiry_type === 'investor' ? (
                      <Briefcase className="text-purple-600" size={20} />
                    ) : (
                      <Mail className="text-green-600" size={20} />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{submission.name}</h3>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        submission.enquiry_type === 'investor' 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {submission.enquiry_type === 'investor' ? 'Investor' : 'Contact'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">{submission.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-sm text-gray-500">{formatDate(submission.submitted_at)}</p>
                  </div>
                  {expandedSubmission === submission.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedSubmission === submission.id && (
                <div className="border-t px-4 py-4 bg-gray-50">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Contact Info */}
                    <div>
                      <h4 className="font-medium mb-3 text-gray-700">Contact Information</h4>
                      <div className="space-y-2 text-sm">
                        <p className="flex items-center gap-2">
                          <User size={16} className="text-gray-400" />
                          <span className="font-medium">{submission.name}</span>
                        </p>
                        <p className="flex items-center gap-2">
                          <Mail size={16} className="text-gray-400" />
                          <a href={`mailto:${submission.email}`} className="text-blue-600 hover:underline">
                            {submission.email}
                          </a>
                        </p>
                        {submission.phone && (
                          <p className="flex items-center gap-2">
                            <Phone size={16} className="text-gray-400" />
                            <a href={`tel:${submission.phone}`} className="text-blue-600 hover:underline">
                              {submission.phone}
                            </a>
                          </p>
                        )}
                        {submission.organization && (
                          <p className="flex items-center gap-2">
                            <Building size={16} className="text-gray-400" />
                            <span>{submission.organization}</span>
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <h4 className="font-medium mb-3 text-gray-700">Message</h4>
                      <div className="bg-white p-3 rounded-lg border text-sm text-gray-700 whitespace-pre-wrap">
                        {submission.message}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-4 pt-4 border-t flex justify-between items-center">
                    <p className="text-xs text-gray-400 flex items-center gap-1">
                      <Clock size={14} />
                      Submitted: {formatDate(submission.submitted_at)}
                    </p>
                    <div className="flex gap-2">
                      <a
                        href={`mailto:${submission.email}?subject=Re: Your ${submission.enquiry_type === 'investor' ? 'Investment Inquiry' : 'Contact Request'}&body=Hi ${submission.name},%0D%0A%0D%0AThank you for reaching out to EvalentX.%0D%0A%0D%0A`}
                        className="flex items-center gap-1 px-3 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600"
                      >
                        <Mail size={16} /> Reply
                      </a>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(submission.id); }}
                        className="flex items-center gap-1 px-3 py-2 bg-red-100 text-red-600 rounded-lg text-sm hover:bg-red-200"
                      >
                        <Trash2 size={16} /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminSubmissionsSection;
